
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import Question from './Question';
import '../styles/Play.css';  // Updated import
import { generateQuestion } from '../api';
import Typed from 'typed.js'; // Add this line

function PlayPage() {  // Renamed from GamePage
  const { subject } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState(null);
  const [error, setError] = useState(null);
  const [unknownTopic, setUnknownTopic] = useState(false);
  const [selectedOption, setSelectedOption] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNewSubjectInput, setShowNewSubjectInput] = useState(false);
  const nextQuestionButtonRef = useRef(null);
  const learnMoreButtonRef = useRef(null);
  const unknownTopicButtonRef = useRef(null);
  const newSubjectInputRef = useRef(null); // Ref for the new subject input
  const [selectedButton, setSelectedButton] = useState(0);
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  useEffect(() => {
    // Always start fetching immediately when a subject is provided
    fetchQuestion(subject);
  }, [subject]);

  useEffect(() => {
    if (showNewSubjectInput && newSubjectInputRef.current) {
      newSubjectInputRef.current.focus(); // Focus the new subject input when shown
    }
  }, [showNewSubjectInput]);

  const fetchQuestion = async (subject) => {
    setError(null);
    setUnknownTopic(false);
    setShowExplanation(false);
    setLoading(true);
    try {
      const subjects = JSON.parse(localStorage.getItem('subjects')) || {};
      if (!subjects[subject]) {
        subjects[subject] = { questions_asked: [], correct_answers: 0 }; // Initialize subject if it doesn't exist
      }
      const askedQuestions = subjects[subject].questions_asked.map(q => q.question) || [];

      const newQuestion = await generateQuestion(subject, askedQuestions);
      
      if (!newQuestion) {
        throw new Error("Failed to generate question");
      }

      if (newQuestion.error === 'unknown_topic') {
        setQuestion(null);
        setUnknownTopic(true);
        setError(newQuestion.message || 'I am not aware of this topic');
        const unknown = JSON.parse(localStorage.getItem('unknown_subjects') || '[]');
        unknown.push({ subject, dateTime: new Date().toISOString() });
        localStorage.setItem('unknown_subjects', JSON.stringify(unknown));
        return;
      }

      // Set the question without shuffling the options
      setQuestion(newQuestion);
      setSelectedOption(0); // Reset selected option to the first option

      // Save the new question in local storage
      subjects[subject].questions_asked.push({
        question: newQuestion.question,
        correct: "no",
        dateTime: new Date().toISOString()
      });
      localStorage.setItem('subjects', JSON.stringify(subjects));
    } catch (err) {
      setError("Error generating question. Please check if the server is running on port 5001.");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (index) => {
    const subjects = JSON.parse(localStorage.getItem('subjects'));
    const correctAnswerKey = question.correct_answer;
    const selectedAnswerKey = Object.keys(question.options)[index];
    const correct = selectedAnswerKey === correctAnswerKey;
    setIsCorrect(correct);
    setShowExplanation(true);
    if (correct) {
      subjects[subject].correct_answers += 1;
    }
    // Update the correct field for the current question
    const currentQuestion = subjects[subject].questions_asked.find(q => q.question === question.question);
    if (currentQuestion) {
      currentQuestion.correct = correct ? "yes" : "no";
    }
    localStorage.setItem('subjects', JSON.stringify(subjects));
  };

  const loadNextQuestion = () => {
    fetchQuestion(subject);
  };

  const handleLearnMore = () => {
    if (question && question.search) {
      const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(question.search)}`;
      window.open(searchUrl, '_blank');
    }
  };

  useHotkeys('ArrowUp', () => {
    if (showExplanation) {
      setSelectedButton((prev) => (prev === 0 ? 1 : 0));
    } else if (question && question.options) {
      const optionsLength = Object.keys(question.options).length;
      setSelectedOption((prev) => {
        const newIndex = prev > 0 ? prev - 1 : optionsLength - 1;
        return newIndex;
      });
    }
  });

  useHotkeys('ArrowDown', () => {
    if (showExplanation) {
      setSelectedButton((prev) => (prev === 0 ? 1 : 0));
    } else if (question && question.options) {
      const optionsLength = Object.keys(question.options).length;
      setSelectedOption((prev) => {
        const newIndex = (prev + 1) % optionsLength;
        return newIndex;
      });
    }
  });

  useHotkeys('Enter', (event) => {
    if (showExplanation) {
      event.preventDefault();
      if (selectedButton === 0) {
        loadNextQuestion();
      } else {
        handleLearnMore();
      }
    }
  });

  useHotkeys('esc', () => navigate('/'));

  useEffect(() => {
    if (showExplanation) {
      const options = {
        strings: [isCorrect ? "Correct!" : "Wrong!"],
        typeSpeed: 30,
        backSpeed: 50,
        loop: false,
        showCursor: false // Add this line to remove the cursor
      };

      const typed = new Typed(".typed-element", options);

      return () => {
        typed.destroy();
      };
    }
  }, [showExplanation, isCorrect]);

  useEffect(() => {
    if (loading) {
      const options = {
        strings: ["Loading..."],
        typeSpeed: 30,
        backSpeed: 50,
        loop: false,
        showCursor: true
      };

      const typed = new Typed(".loading-typed-element", options);

      return () => {
        typed.destroy();
      };
    }
  }, [loading]);

  useEffect(() => {
    if (showExplanation) {
      if (selectedButton === 0 && nextQuestionButtonRef.current) {
        nextQuestionButtonRef.current.focus();
      } else if (selectedButton === 1 && learnMoreButtonRef.current) {
        learnMoreButtonRef.current.focus();
      }
    }
  }, [showExplanation, selectedButton]);

  useEffect(() => {
    if (unknownTopic && !loading && !question && unknownTopicButtonRef.current) {
      unknownTopicButtonRef.current.focus();
    }
  }, [unknownTopic, loading, question]);

  const handleKeyboardSelection = (index) => {
    setSelectedOption(index);
  };

  const handleNewSubjectSubmit = (event) => {
    event.preventDefault();
    const newSubject = event.target.elements.newSubject.value.trim();
    if (newSubject) {
      navigate(`/play/${encodeURIComponent(newSubject)}`);
    }
  };

  return (
    <div className="play-page">  {/* Updated class name */}
      {showNewSubjectInput ? (
        <form onSubmit={handleNewSubjectSubmit} className="center">
          <input
            type="text"
            name="newSubject"
            placeholder="Enter a new subject"
            required
            ref={newSubjectInputRef}
            autoFocus // Add autofocus here
          />
          <button type="submit">Start Game</button>
          <p className="bottom-center-message">
            Press 'ESC' to go back to the main menu
          </p>
        </form>
      ) : (
        <>
          <div>
            <span className="subject-label">{subject}</span>

            {isTouchDevice && (
              <button className="fab" onClick={() => navigate('/')}>&lt;</button>
            )}
          </div>
          {loading ? (
            <div className="container center">
              <div className="centertext">
                <span className="loading-typed-element centertext"></span>
              </div>
            </div>
          ) : question ? (
            showExplanation ? (
              <div className="container center">
                <div className="explanation">
                  <h2 className={`typed-element result-label ${isCorrect ? '' : 'wrong-text'}`}></h2>
                  {!isCorrect && (
                    <p className="correct-answer">Correct answer: {question.options[question.correct_answer]}</p>
                  )}
                  <p className="explanation-text">Explanation: {question.explanation}</p>
                  <button
                    ref={nextQuestionButtonRef}
                    className={`option center ${selectedButton === 0 ? 'selected-option' : ''}`}
                    onClick={loadNextQuestion}
                  >
                    Next Question
                  </button>
                  <button
                    ref={learnMoreButtonRef}
                    className={`option center ${selectedButton === 1 ? 'selected-option' : ''}`}
                    onClick={handleLearnMore}
                  >
                    Learn more about this
                  </button>
                </div>
              </div>
            ) : (
              <Question
                question={question.question}
                options={Object.values(question.options)}
                selectedOptionIndex={selectedOption}
                setSelectedOptionIndex={handleKeyboardSelection}
                handleAnswer={handleAnswer}
              />
            )
          ) : (
            <div className="container center">
              {error ? (
                <div className="centertext">
                  {unknownTopic ? (
                    <>
                      {error && (
                        <h3 className="text-danger mt-sm">{error}</h3>
                      )}
                      <div className="mt-lg">
                        <button
                          ref={unknownTopicButtonRef}
                          className="option center selected-option"
                          autoFocus
                          onClick={() => navigate('/', { state: { openNewSubjectInput: true } })}
                        >
                          Enter a new topic
                        </button>
                      </div>
                    </>
                  ) : (
                    <h3 className="text-danger">{error}</h3>
                  )}
                </div>
              ) : (
                <div>
                  <span className='centertext'>XXX</span>
                </div>
              )}
            </div>
          )}
          {!unknownTopic && (
            <p className="bottom-center-message">
              Press 'ESC' to go back to the main menu
            </p>
          )}
        </>
      )}
      {showNewSubjectInput && (
        <p className="bottom-center-message">
          Press 'N' to add a new subject
        </p>
      )}
    </div>
  );
}

export default PlayPage;